import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
  Bell, Mail, Smartphone, MessageCircle, Settings, Save, RefreshCw,
  FileText, Truck, CreditCard, Users, AlertTriangle, Clock, Info, Lock,
  CheckCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import PhoneOtpModal from '../components/PhoneOtpModal';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  SYSTEME: Settings, DEVIS: FileText, LOGISTIQUE: Truck,
  TERRAIN: Users, FINANCE: CreditCard, transfers: Truck,
  contracts: FileText, billing: CreditCard, incidents: AlertTriangle
};

const CHANNEL_INFO = {
  in_app: { name: 'App', icon: Bell, color: 'text-blue-600' },
  email: { name: 'Email', icon: Mail, color: 'text-green-600' },
  sms: { name: 'SMS', icon: Smartphone, color: 'text-orange-600' },
  whatsapp: { name: 'WhatsApp', icon: MessageCircle, color: 'text-emerald-600' }
};

// Fallback hardcoded events if no templates from backend
const FALLBACK_EVENTS = {
  transfers: {
    label: 'Transferts', icon: Truck,
    events: [
      { key: 'transfer_created', label: 'Nouveau transfert planifié', description: 'Quand un transfert est créé', critical: false },
      { key: 'transfer_in_transit', label: 'En transit', description: 'Quand le transfert part en livraison', critical: false },
      { key: 'transfer_delivered', label: 'Livraison effectuée', description: 'Quand le transfert est livré', critical: false },
      { key: 'transfer_cancelled', label: 'Transfert annulé', description: 'Quand un transfert est annulé', critical: false }
    ]
  },
  contracts: {
    label: 'Contrats', icon: FileText,
    events: [
      { key: 'contract_created', label: 'Contrat créé', description: 'Quand un nouveau contrat est créé', critical: false },
      { key: 'contract_suspended', label: 'Contrat suspendu', description: 'Quand le contrat est suspendu', critical: true },
      { key: 'contract_expiry_reminder', label: 'Rappel d\'expiration', description: '30 et 7 jours avant expiration', critical: false }
    ]
  },
  billing: {
    label: 'Facturation', icon: CreditCard,
    events: [
      { key: 'contract_invoice_generated', label: 'Nouvelle facture', description: 'Quand une facture est générée', critical: false },
      { key: 'contract_invoice_overdue', label: 'Facture en retard', description: 'Quand une facture est en retard', critical: true }
    ]
  },
  incidents: {
    label: 'Incidents', icon: AlertTriangle,
    events: [
      { key: 'incident_reported', label: 'Incident signalé', description: 'Quand un incident est signalé sur un transfert', critical: true }
    ]
  }
};

const NotificationSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [phoneVerified, setPhoneVerified] = useState(true);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [userPhone, setUserPhone] = useState('');

  useEffect(() => { fetchPreferences(); fetchPhoneStatus(); }, []);

  const fetchPhoneStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/phone-verification/status`, {});
      setPhoneVerified(!!res.data?.is_verified);
      setUserPhone(res.data?.phone || user?.phone || user?.telephone || '');
    } catch { setPhoneVerified(false); setUserPhone(user?.phone || user?.telephone || ''); }
  };

  // Après vérification OTP réussie : activer automatiquement le canal WhatsApp.
  const handleOtpVerified = async (phone) => {
    setPhoneVerified(true);
    if (phone) setUserPhone(phone);
    try {
      await axios.put(`${API}/api/notification-preferences/me`, { ...preferences, whatsapp_enabled: true }, {});
      setPreferences(prev => ({ ...prev, whatsapp_enabled: true }));
      toast.success('WhatsApp activé ✅');
    } catch (e) {
      toast.error("Numéro vérifié, mais l'activation WhatsApp a échoué. Réessayez.");
    }
  };

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/notification-preferences/me`, {});
      setPreferences({
        notifications_enabled: res.data?.preferences?.notifications_enabled ?? res.data?.notifications_enabled ?? true,
        email_enabled: res.data?.preferences?.email_enabled ?? res.data?.email_enabled ?? true,
        sms_enabled: res.data?.preferences?.sms_enabled ?? res.data?.sms_enabled ?? false,
        whatsapp_enabled: res.data?.preferences?.whatsapp_enabled ?? res.data?.whatsapp_enabled ?? false,
        digest_mode: res.data?.preferences?.digest_mode ?? res.data?.digest_mode ?? 'daily',
        event_preferences: res.data?.preferences?.event_preferences || [],
        contract_notification_defaults: res.data?.preferences?.contract_notification_defaults || res.data?.contract_notification_defaults || {}
      });
      setTemplates(res.data?.available_templates || []);
    } catch {
      setPreferences({
        notifications_enabled: true, email_enabled: true, sms_enabled: false,
        whatsapp_enabled: false,
        digest_mode: 'daily', event_preferences: [], contract_notification_defaults: {}
      });
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/api/notification-preferences/me`, preferences, {});
      toast.success('Préférences enregistrées');
    } catch {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setPreferences(prev => ({ ...prev, [field]: value }));
  };

  const toggleEventEnabled = (eventKey) => {
    setPreferences(prev => ({
      ...prev,
      contract_notification_defaults: {
        ...prev.contract_notification_defaults,
        [eventKey]: {
          ...prev.contract_notification_defaults[eventKey],
          enabled: !(prev.contract_notification_defaults[eventKey]?.enabled ?? true)
        }
      }
    }));
  };

  const toggleEventChannel = (eventKey, channel) => {
    const current = preferences.contract_notification_defaults[eventKey]?.channels || ['in_app'];
    const newChannels = current.includes(channel)
      ? current.filter(c => c !== channel)
      : [...current, channel];
    setPreferences(prev => ({
      ...prev,
      contract_notification_defaults: {
        ...prev.contract_notification_defaults,
        [eventKey]: { ...prev.contract_notification_defaults[eventKey], channels: newChannels }
      }
    }));
  };

  const isEventEnabled = (key) => preferences?.contract_notification_defaults[key]?.enabled ?? true;
  const isChannelEnabled = (key, ch) => (preferences?.contract_notification_defaults[key]?.channels || ['in_app']).includes(ch);

  // Group templates by category if available, else use fallback
  const hasTemplates = templates.length > 0;
  const groupedTemplates = hasTemplates
    ? templates.reduce((acc, t) => { const c = t.category || 'AUTRE'; (acc[c] = acc[c] || []).push(t); return acc; }, {})
    : null;

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="notification-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-6 h-6" />
            Préférences de notification
          </h1>
          <p className="text-gray-500 mt-1">Configurez comment et quand vous souhaitez être notifié</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPreferences} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={savePreferences} disabled={saving} className="bg-[#1db584] hover:bg-[#16a075]" data-testid="save-preferences-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Global Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-400" />
            Paramètres généraux
          </CardTitle>
          <CardDescription>Activez ou désactivez les notifications par canal</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Master toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium">Toutes les notifications</p>
                <p className="text-sm text-gray-500">Activer/désactiver toutes les notifications</p>
              </div>
            </div>
            <Switch
              checked={preferences?.notifications_enabled ?? true}
              onCheckedChange={(v) => updateField('notifications_enabled', v)}
              data-testid="notifications-master-toggle"
            />
          </div>

          <Separator />

          {/* Channels */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium">Email</p>
                  <p className="text-xs text-gray-500">Recevoir par email</p>
                </div>
              </div>
              <Switch
                checked={preferences?.email_enabled ?? true}
                onCheckedChange={(v) => updateField('email_enabled', v)}
                disabled={!preferences?.notifications_enabled}
                data-testid="email-toggle"
              />
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <Smartphone className="w-5 h-5 text-orange-600" />
                <div>
                  <p className="font-medium">SMS</p>
                  <p className="text-xs text-gray-500">Recevoir par SMS</p>
                </div>
              </div>
              <Switch
                checked={preferences?.sms_enabled ?? false}
                onCheckedChange={(v) => updateField('sms_enabled', v)}
                disabled={!preferences?.notifications_enabled}
                data-testid="sms-toggle"
              />
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="font-medium">WhatsApp</p>
                  {phoneVerified
                    ? <p className="text-xs text-gray-500">Recevoir par WhatsApp</p>
                    : <button onClick={() => setOtpModalOpen(true)} className="text-xs text-amber-600 hover:underline flex items-center gap-1" data-testid="whatsapp-verify-link"><Lock className="w-3 h-3" />Vérifiez votre numéro pour activer</button>}
                </div>
              </div>
              <Switch
                checked={preferences?.whatsapp_enabled ?? false}
                onCheckedChange={(v) => {
                  if (v && !phoneVerified) { setOtpModalOpen(true); return; }
                  updateField('whatsapp_enabled', v);
                }}
                disabled={!preferences?.notifications_enabled}
                data-testid="whatsapp-toggle"
              />
            </div>
          </div>

          <Separator />

          {/* Digest Mode */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Fréquence des emails
            </h3>
            <p className="text-sm text-gray-500 mb-4">Recevez les notifications en temps réel ou groupées</p>
            <RadioGroup
              value={preferences?.digest_mode || 'daily'}
              onValueChange={(v) => updateField('digest_mode', v)}
              className="grid grid-cols-1 md:grid-cols-3 gap-4"
              data-testid="digest-mode-radio"
            >
              {[
                { value: 'realtime', label: 'Temps réel', desc: 'Chaque événement' },
                { value: 'daily', label: 'Quotidien', desc: 'Résumé à 8h', recommended: true },
                { value: 'weekly', label: 'Hebdomadaire', desc: 'Résumé le lundi' }
              ].map((opt) => (
                <Label
                  key={opt.value}
                  htmlFor={opt.value}
                  className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                    preferences?.digest_mode === opt.value ? 'border-[#1db584] bg-[#1db584]/5' : 'hover:bg-gray-50'
                  }`}
                >
                  <RadioGroupItem value={opt.value} id={opt.value} />
                  <div>
                    <p className="font-medium">{opt.label}</p>
                    <p className="text-xs text-gray-500">{opt.desc}</p>
                  </div>
                  {opt.recommended && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 ml-auto">
                      Recommandé
                    </Badge>
                  )}
                </Label>
              ))}
            </RadioGroup>
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-700">
                Le mode digest regroupe vos notifications en un seul email. Les notifications critiques sont toujours envoyées immédiatement.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Event Preferences - API-driven templates OR fallback hardcoded */}
      {hasTemplates ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notifications par événement
            </CardTitle>
            <CardDescription>Personnalisez chaque type de notification</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={Object.keys(groupedTemplates)[0]} className="w-full">
              <TabsList className="w-full flex flex-wrap h-auto gap-1 mb-4">
                {Object.keys(groupedTemplates).map(cat => {
                  const Icon = CATEGORY_ICONS[cat] || Bell;
                  return (
                    <TabsTrigger key={cat} value={cat} className="flex items-center gap-1">
                      <Icon className="h-4 w-4" />
                      {cat}
                      <Badge variant="secondary" className="ml-1 h-5 px-1.5">{groupedTemplates[cat].length}</Badge>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              {Object.entries(groupedTemplates).map(([cat, catTemplates]) => (
                <TabsContent key={cat} value={cat} className="space-y-3">
                  {catTemplates.map(template => {
                    const pref = preferences?.event_preferences?.find(ep => ep.event_type === template.event_type);
                    const enabled = pref?.enabled ?? true;
                    const channels = pref?.channels || template.default_channels || ['in_app', 'email'];
                    return (
                      <div key={template.event_type} className={`p-4 border rounded-lg ${!enabled ? 'opacity-50 bg-gray-50' : ''}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{template.name}</p>
                              {template.is_mandatory && <Badge variant="outline" className="text-xs">Obligatoire</Badge>}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                          </div>
                          <Switch
                            checked={enabled}
                            onCheckedChange={() => {
                              const ch = channels.join(',');
                              axios.put(
                                `${API}/api/notification-preferences/me/event/${template.event_type}?enabled=${!enabled}&channels=${ch}`,
                                {},
                                {}
                              ).then(() => {
                                setPreferences(prev => {
                                  const eps = [...(prev.event_preferences || [])];
                                  const idx = eps.findIndex(ep => ep.event_type === template.event_type);
                                  if (idx >= 0) eps[idx] = { ...eps[idx], enabled: !enabled };
                                  else eps.push({ event_type: template.event_type, enabled: !enabled, channels });
                                  return { ...prev, event_preferences: eps };
                                });
                              }).catch(() => {});
                            }}
                            disabled={template.is_mandatory || !preferences?.notifications_enabled}
                          />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(CHANNEL_INFO).map(([ch, info]) => {
                            const ChIcon = info.icon;
                            const isActive = channels.includes(ch);
                            const isDisabled = ch === 'in_app' || !enabled;
                            return (
                              <button
                                key={ch}
                                onClick={() => {
                                  if (isDisabled) return;
                                  const newCh = isActive ? channels.filter(c => c !== ch) : [...channels, ch];
                                  axios.put(
                                    `${API}/api/notification-preferences/me/event/${template.event_type}?enabled=${enabled}&channels=${newCh.join(',')}`,
                                    {},
                                    {}
                                  ).then(() => {
                                    setPreferences(prev => {
                                      const eps = [...(prev.event_preferences || [])];
                                      const idx = eps.findIndex(ep => ep.event_type === template.event_type);
                                      if (idx >= 0) eps[idx] = { ...eps[idx], channels: newCh };
                                      else eps.push({ event_type: template.event_type, enabled, channels: newCh });
                                      return { ...prev, event_preferences: eps };
                                    });
                                  }).catch(() => {});
                                }}
                                disabled={isDisabled}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all ${
                                  isActive ? 'bg-[#1db584] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                } ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                              >
                                <ChIcon className="h-3.5 w-3.5" />
                                {info.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      ) : (
        /* Fallback: hardcoded events */
        Object.entries(FALLBACK_EVENTS).map(([catKey, cat]) => {
          const CatIcon = cat.icon;
          return (
            <Card key={catKey}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CatIcon className="w-5 h-5 text-gray-400" />
                  {cat.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {cat.events.map((event) => (
                    <div key={event.key} className={`p-4 border rounded-lg ${event.critical ? 'bg-amber-50 border-amber-200' : ''}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">{event.label}</p>
                            {event.critical && (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                                <Lock className="w-3 h-3 mr-1" />
                                Obligatoire
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{event.description}</p>
                          {!event.critical && isEventEnabled(event.key) && (
                            <div className="flex items-center gap-4 mt-3">
                              <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={isChannelEnabled(event.key, 'in_app')}
                                  onChange={(e) => toggleEventChannel(event.key, 'in_app')}
                                  disabled={!preferences?.notifications_enabled}
                                  className="rounded border-gray-300 text-[#1db584] focus:ring-[#1db584]" />
                                <Bell className="w-4 h-4 text-gray-400" />In-app
                              </label>
                              <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={isChannelEnabled(event.key, 'email')}
                                  onChange={(e) => toggleEventChannel(event.key, 'email')}
                                  disabled={!preferences?.notifications_enabled || !preferences?.email_enabled}
                                  className="rounded border-gray-300 text-[#1db584] focus:ring-[#1db584]" />
                                <Mail className="w-4 h-4 text-gray-400" />Email
                              </label>
                              <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={isChannelEnabled(event.key, 'whatsapp')}
                                  onChange={(e) => toggleEventChannel(event.key, 'whatsapp')}
                                  disabled={!preferences?.notifications_enabled || !preferences?.whatsapp_enabled}
                                  data-testid={`channel-whatsapp-${event.key}`}
                                  className="rounded border-gray-300 text-[#1db584] focus:ring-[#1db584]" />
                                <MessageCircle className="w-4 h-4 text-emerald-500" />WhatsApp
                              </label>
                            </div>
                          )}
                        </div>
                        {event.critical ? (
                          <div className="flex items-center gap-2 text-amber-600">
                            <CheckCircle className="w-5 h-5" />
                            <span className="text-sm font-medium">Toujours actif</span>
                          </div>
                        ) : (
                          <Switch
                            checked={isEventEnabled(event.key)}
                            onCheckedChange={() => toggleEventEnabled(event.key)}
                            disabled={!preferences?.notifications_enabled}
                            data-testid={`toggle-${event.key}`}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">À propos des notifications</p>
              <ul className="mt-2 text-sm text-blue-800 space-y-1">
                <li>Les notifications in-app sont toujours actives</li>
                <li>Les notifications obligatoires ne peuvent pas être désactivées</li>
                <li>WhatsApp et SMS nécessitent un numéro de téléphone vérifié et votre consentement</li>
                <li>Répondez STOP sur WhatsApp pour vous désabonner à tout moment</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bottom save */}
      <div className="flex justify-end pb-6">
        <Button onClick={savePreferences} disabled={saving} className="bg-[#1db584] hover:bg-[#16a075]" size="lg" data-testid="save-preferences-btn-bottom">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer les préférences
        </Button>
      </div>

      <PhoneOtpModal
        open={otpModalOpen}
        onClose={() => setOtpModalOpen(false)}
        defaultPhone={userPhone}
        onVerified={handleOtpVerified}
      />
    </div>
  );
};

export default NotificationSettings;
